import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { NeonButton } from "@/components/ui/NeonButton";
import { NeonCard, NeonCardContent } from "@/components/ui/NeonCard";
import { Link } from "react-router-dom";

export default function AboutPage() {
  return (
    <>
      <MarketingHeader />
      <main className="min-h-screen gradient-dark pt-24">
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/marketing/victor-freitas-WvDYdXDzkhs-unsplash.jpg"
              alt="Athlete training"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/95 via-dark-bg/80 to-dark-bg" />
          </div>

          {/* Centered stripes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/25 to-transparent"
              style={{ left: "calc(50% - 220px)" }}
            />
            <div
              className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent"
              style={{ left: "calc(50% + 220px)" }}
            />
          </div>

          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight">
              About <span className="text-primary">Frequent Health</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-light mt-4 max-w-3xl mx-auto">
              We help people train smarter with expert-designed, AI-adaptive programs — built
              for real life, at your speed.
            </p>
            <div className="mt-8 flex items-center justify-center">
              <Link to="/signup">
                <NeonButton className="px-8 py-4 text-base animate-pulse-glow text-black">
                  Activate Health
                </NeonButton>
              </Link>
            </div>
          </div>
        </section>

        {/* Mission / story */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <SectionHeader
              eyebrow="Why we exist"
              title={
                <>
                  Training hard shouldn’t mean training{" "}
                  <span className="text-primary">blind</span>.
                </>
              }
              subtitle="Generic plans and impersonal gyms miss what matters: your goals, recovery, constraints, and motivation."
            />

            <div className="grid lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
              <NeonCard className="h-full">
                <NeonCardContent className="space-y-4">
                  <h3 className="text-2xl font-bold text-white">The problem we saw</h3>
                  <p className="text-gray-light leading-relaxed">
                    Most programs are built for averages. Life isn’t. When your plan can’t adapt
                    to your schedule, energy, and recovery, it’s easy to overtrain, stall out,
                    and get frustrated.
                  </p>
                </NeonCardContent>
              </NeonCard>

              <NeonCard className="h-full">
                <NeonCardContent className="space-y-4">
                  <h3 className="text-2xl font-bold text-white">What we built instead</h3>
                  <p className="text-gray-light leading-relaxed">
                    Frequent Health combines{" "}
                    <span className="text-primary font-semibold">20+ years</span> of expert
                    training knowledge with{" "}
                    <span className="text-primary font-semibold">proprietary AI</span> to create a
                    program that adapts and grows with you — week after week.
                  </p>
                </NeonCardContent>
              </NeonCard>
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-primary/10">
          <div className="container mx-auto px-6">
            <SectionHeader
              eyebrow="Our mission"
              title={
                <>
                  Make <span className="text-primary">smarter training</span> the default.
                </>
              }
              subtitle="We want your program to be clear, personalized, and sustainable — so you can focus on execution and progress."
            />

            <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
              <NeonCard className="h-full">
                <NeonCardContent className="space-y-3">
                  <h3 className="text-xl font-bold text-white">Personalized pathway</h3>
                  <p className="text-gray-light leading-relaxed">
                    Your goals, limitations, and life constraints shape the plan — not the other
                    way around.
                  </p>
                </NeonCardContent>
              </NeonCard>
              <NeonCard className="h-full">
                <NeonCardContent className="space-y-3">
                  <h3 className="text-xl font-bold text-white">Adaptive by design</h3>
                  <p className="text-gray-light leading-relaxed">
                    As your capacity changes, your program recalibrates so you keep improving
                    without burning out.
                  </p>
                </NeonCardContent>
              </NeonCard>
              <NeonCard className="h-full">
                <NeonCardContent className="space-y-3">
                  <h3 className="text-xl font-bold text-white">Built for consistency</h3>
                  <p className="text-gray-light leading-relaxed">
                    The best plan is the one you can follow. We optimize for clarity and
                    momentum, not complexity.
                  </p>
                </NeonCardContent>
              </NeonCard>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="relative py-20 bg-dark-bg overflow-hidden border-t border-primary/10">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-dark-bg via-dark-bg/80 to-dark-bg" />
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-5xl md:text-6xl font-black text-white leading-tight">
                Train smarter.{" "}
                <span className="text-primary">Level up</span>.
              </h2>
              <p className="text-xl text-gray-light">
                Expert-designed, AI-adaptive programs creating your pathway to fitness.
              </p>
              <Link to="/signup">
                <NeonButton className="px-8 py-4 text-base animate-pulse-glow text-black mt-4">
                  Activate Health
                </NeonButton>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </>
  );
}

